import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Chart } from "primereact/chart";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Skeleton } from "primereact/skeleton";
import { Toast } from "primereact/toast";
import html2pdf from "html2pdf.js";
import ReportService from "../../services/ReportService";

const moneyFormatter = new Intl.NumberFormat("tr-TR", {
  style: "currency",
  currency: "TRY",
  maximumFractionDigits: 2,
});

const numberFormatter = new Intl.NumberFormat("tr-TR");

const formatMoney = (value) => moneyFormatter.format(Number(value) || 0);
const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("tr-TR");
};

const toLocalYmd = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const startOfWeek = (date) => {
  const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = result.getDay() || 7;
  result.setDate(result.getDate() - day + 1);
  return result;
};

const createPresetRange = (preset) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (preset) {
    case "today":
      return [today, today];
    case "week":
      return [startOfWeek(today), today];
    case "last_month":
      return [
        new Date(today.getFullYear(), today.getMonth() - 1, 1),
        new Date(today.getFullYear(), today.getMonth(), 0),
      ];
    case "year":
      return [new Date(today.getFullYear(), 0, 1), today];
    case "month":
    default:
      return [new Date(today.getFullYear(), today.getMonth(), 1), today];
  }
};

const periodPresets = [
  {
    label: "Bugün",
    value: "today",
    icon: "pi pi-sun",
    activeClass: "!border-blue-700 !bg-blue-700 !text-white shadow-md shadow-blue-500/25 dark:!border-blue-400 dark:!bg-blue-500 dark:!text-white",
    idleClass: "!border-blue-200 !bg-blue-50 !text-blue-700 hover:!border-blue-400 hover:!bg-blue-100 dark:!border-blue-900 dark:!bg-blue-950/50 dark:!text-blue-300",
  },
  {
    label: "Bu Hafta",
    value: "week",
    icon: "pi pi-calendar",
    activeClass: "!border-violet-700 !bg-violet-700 !text-white shadow-md shadow-violet-500/25 dark:!border-violet-400 dark:!bg-violet-500 dark:!text-white",
    idleClass: "!border-violet-200 !bg-violet-50 !text-violet-700 hover:!border-violet-400 hover:!bg-violet-100 dark:!border-violet-900 dark:!bg-violet-950/50 dark:!text-violet-300",
  },
  {
    label: "Bu Ay",
    value: "month",
    icon: "pi pi-calendar-clock",
    activeClass: "!border-emerald-700 !bg-emerald-700 !text-white shadow-md shadow-emerald-500/25 dark:!border-emerald-400 dark:!bg-emerald-500 dark:!text-white",
    idleClass: "!border-emerald-200 !bg-emerald-50 !text-emerald-700 hover:!border-emerald-400 hover:!bg-emerald-100 dark:!border-emerald-900 dark:!bg-emerald-950/50 dark:!text-emerald-300",
  },
  {
    label: "Geçen Ay",
    value: "last_month",
    icon: "pi pi-history",
    activeClass: "!border-amber-700 !bg-amber-700 !text-white shadow-md shadow-amber-500/25 dark:!border-amber-400 dark:!bg-amber-500 dark:!text-white",
    idleClass: "!border-amber-200 !bg-amber-50 !text-amber-800 hover:!border-amber-400 hover:!bg-amber-100 dark:!border-amber-900 dark:!bg-amber-950/50 dark:!text-amber-300",
  },
  {
    label: "Bu Yıl",
    value: "year",
    icon: "pi pi-chart-line",
    activeClass: "!border-rose-700 !bg-rose-700 !text-white shadow-md shadow-rose-500/25 dark:!border-rose-400 dark:!bg-rose-500 dark:!text-white",
    idleClass: "!border-rose-200 !bg-rose-50 !text-rose-700 hover:!border-rose-400 hover:!bg-rose-100 dark:!border-rose-900 dark:!bg-rose-950/50 dark:!text-rose-300",
  },
];

const getPayload = (response) => {
  const result = response?.data ?? response;
  if (result?.success === false) throw new Error(result.message || "Rapor oluşturulamadı.");
  return result?.data ?? result;
};

const escapeCsv = (value) => `"${String(value ?? "").replaceAll('"', '""')}"`;
const escapeHtml = (value) => String(value ?? "")
  .replaceAll("&", "&amp;")
  .replaceAll("<", "&lt;")
  .replaceAll(">", "&gt;")
  .replaceAll('"', "&quot;")
  .replaceAll("'", "&#039;");

const buildComparison = (currentValue, previousValue) => {
  const current = Number(currentValue) || 0;
  const previous = Number(previousValue) || 0;
  if (Math.abs(previous) < 0.001) {
    if (Math.abs(current) < 0.001) {
      return { label: "Önceki dönemle aynı", icon: "pi pi-minus", className: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300" };
    }
    return { label: "Önceki dönemde yok", icon: "pi pi-sparkles", className: "bg-blue-100 text-blue-700 dark:bg-blue-900/60 dark:text-blue-300" };
  }
  const change = ((current - previous) / Math.abs(previous)) * 100;
  if (Math.abs(change) < 0.05) {
    return { label: "Önceki dönemle aynı", icon: "pi pi-minus", className: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300" };
  }
  const isUp = change > 0;
  return {
    label: `%${Math.abs(change).toLocaleString("tr-TR", { maximumFractionDigits: 1 })} ${isUp ? "arttı" : "azaldı"}`,
    icon: isUp ? "pi pi-arrow-up" : "pi pi-arrow-down",
    className: isUp
      ? "bg-blue-100 text-blue-700 dark:bg-blue-900/60 dark:text-blue-300"
      : "bg-rose-100 text-rose-700 dark:bg-rose-900/60 dark:text-rose-300",
  };
};

function MetricCard({ label, value, description, icon, tone, emphasis = false, comparison, onClick }) {
  const content = (
    <>
      <span className={`pointer-events-none absolute -right-5 -top-7 h-20 w-20 rounded-full blur-2xl ${tone.glow}`} aria-hidden="true" />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="block text-[9px] font-black uppercase tracking-[0.14em] text-slate-600 dark:text-slate-300">{label}</span>
          <strong className={`mt-2 block truncate font-black tracking-tight ${emphasis ? "text-xl" : "text-lg"} ${tone.text}`}>{formatMoney(value)}</strong>
          <span className="mt-1 block truncate text-[9px] font-semibold text-slate-500 dark:text-slate-400">{description}</span>
          {comparison && (
            <span className={`mt-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[8px] font-black ${comparison.className}`}>
              <i className={comparison.icon} aria-hidden="true" />
              {comparison.label}
            </span>
          )}
        </div>
        <span className={`flex !h-9 !w-9 shrink-0 items-center justify-center rounded-xl ${tone.icon}`}>
          <i className={`${icon} text-sm`} />
        </span>
      </div>
      {onClick && (
        <span className="relative mt-3 flex items-center gap-1 text-[8px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400">
          Detayları gör <i className="pi pi-arrow-right text-[7px]" />
        </span>
      )}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`relative w-full cursor-pointer appearance-none overflow-hidden rounded-2xl border border-solid bg-white p-4 text-left shadow-sm [font-family:inherit] transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 motion-reduce:transform-none dark:bg-slate-900 dark:focus-visible:ring-offset-slate-950 ${tone.card}`}
      >
        {content}
      </button>
    );
  }

  return (
    <article className={`relative overflow-hidden rounded-2xl border p-4 shadow-sm ${tone.card}`}>
      {content}
    </article>
  );
}

function InsightCard({ label, value, detail, icon, color }) {
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-slate-50/80 p-3 dark:border-slate-700/80 dark:bg-slate-800/60">
      <div className="flex items-start gap-2.5">
        <span className={`flex !h-8 !w-8 shrink-0 items-center justify-center rounded-lg ${color}`} aria-hidden="true">
          <i className={`${icon} text-xs`} />
        </span>
        <div className="min-w-0">
          <span className="block text-[8px] font-black uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">{label}</span>
          <strong className="mt-1 block truncate text-sm font-black text-slate-900 dark:text-white">{value}</strong>
          <span className="mt-0.5 block truncate text-[9px] font-semibold text-slate-500 dark:text-slate-400">{detail}</span>
        </div>
      </div>
    </div>
  );
}

const tones = {
  emerald: {
    card: "border-emerald-200/80 bg-gradient-to-br from-emerald-50 to-white dark:border-emerald-900/60 dark:from-emerald-950/40 dark:to-slate-900",
    text: "text-emerald-700 dark:text-emerald-300",
    icon: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-300",
    glow: "bg-emerald-300/30 dark:bg-emerald-800/20",
  },
  blue: {
    card: "border-blue-200/80 bg-gradient-to-br from-blue-50 to-white dark:border-blue-900/60 dark:from-blue-950/40 dark:to-slate-900",
    text: "text-blue-700 dark:text-blue-300",
    icon: "bg-blue-100 text-blue-700 dark:bg-blue-900/60 dark:text-blue-300",
    glow: "bg-blue-300/30 dark:bg-blue-800/20",
  },
  cyan: {
    card: "border-cyan-200/80 bg-gradient-to-br from-cyan-50 to-white dark:border-cyan-900/60 dark:from-cyan-950/40 dark:to-slate-900",
    text: "text-cyan-700 dark:text-cyan-300",
    icon: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/60 dark:text-cyan-300",
    glow: "bg-cyan-300/30 dark:bg-cyan-800/20",
  },
  violet: {
    card: "border-violet-200/80 bg-gradient-to-br from-violet-50 to-white dark:border-violet-900/60 dark:from-violet-950/40 dark:to-slate-900",
    text: "text-violet-700 dark:text-violet-300",
    icon: "bg-violet-100 text-violet-700 dark:bg-violet-900/60 dark:text-violet-300",
    glow: "bg-violet-300/30 dark:bg-violet-800/20",
  },
  rose: {
    card: "border-rose-200/80 bg-gradient-to-br from-rose-50 to-white dark:border-rose-900/60 dark:from-rose-950/40 dark:to-slate-900",
    text: "text-rose-700 dark:text-rose-300",
    icon: "bg-rose-100 text-rose-700 dark:bg-rose-900/60 dark:text-rose-300",
    glow: "bg-rose-300/30 dark:bg-rose-800/20",
  },
  amber: {
    card: "border-amber-200/80 bg-gradient-to-br from-amber-50 to-white dark:border-amber-900/60 dark:from-amber-950/40 dark:to-slate-900",
    text: "text-amber-700 dark:text-amber-300",
    icon: "bg-amber-100 text-amber-700 dark:bg-amber-900/60 dark:text-amber-300",
    glow: "bg-amber-300/30 dark:bg-amber-800/20",
  },
  slate: {
    card: "border-slate-200 bg-gradient-to-br from-slate-50 to-white dark:border-slate-700 dark:from-slate-800 dark:to-slate-900",
    text: "text-slate-800 dark:text-slate-100",
    icon: "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200",
    glow: "bg-slate-300/30 dark:bg-slate-700/20",
  },
};

function AgingPanel({ title, subtitle, data, color }) {
  const rows = [
    { key: "overdue", label: "Gecikmiş", dot: "bg-rose-500" },
    { key: "dueToday", label: "Bugün", dot: "bg-orange-500" },
    { key: "nextSevenDays", label: "1–7 gün", dot: "bg-amber-400" },
    { key: "nextThirtyDays", label: "8–30 gün", dot: "bg-blue-500" },
    { key: "later", label: "30 gün sonrası", dot: "bg-indigo-500" },
    { key: "noDueDate", label: "Vadesiz", dot: "bg-slate-400" },
  ];
  const total = rows.reduce((sum, row) => sum + (Number(data?.[row.key]) || 0), 0);

  return (
    <section className="rounded-[1.75rem] border border-slate-200/80 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h3 className="m-0 text-base font-black text-slate-900 dark:text-white">{title}</h3>
          <p className="mb-0 mt-1 text-[10px] font-semibold text-slate-500 dark:text-slate-400">{subtitle}</p>
        </div>
        <span className={`rounded-full px-2.5 py-1 text-[10px] font-black ${color}`}>{formatMoney(total)}</span>
      </div>
      <div className="space-y-3">
        {rows.map((row) => {
          const value = Number(data?.[row.key]) || 0;
          const percentage = total > 0 ? Math.min(100, (value / total) * 100) : 0;
          return (
            <div key={row.key}>
              <div className="mb-1.5 flex items-center justify-between gap-3">
                <span className="flex items-center gap-2 text-[10px] font-bold text-slate-600 dark:text-slate-300">
                  <span className={`h-2 w-2 rounded-full ${row.dot}`} />
                  {row.label}
                </span>
                <span className="text-[10px] font-black text-slate-800 dark:text-slate-100">{formatMoney(value)}</span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                <div className={`h-full rounded-full ${row.dot}`} style={{ width: `${percentage}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default function FinancialOverviewReportPage() {
  const navigate = useNavigate();
  const toast = useRef(null);
  const requestSequence = useRef(0);
  const [mounted, setMounted] = useState(false);
  const [dateRange, setDateRange] = useState(createPresetRange("month"));
  const [selectedPreset, setSelectedPreset] = useState("month");
  const [selectedBranchId, setSelectedBranchId] = useState(0);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customerSearch, setCustomerSearch] = useState("");
  const [supplierSearch, setSupplierSearch] = useState("");
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailType, setDetailType] = useState("");
  const [detailData, setDetailData] = useState({ title: "", items: [], totalCount: 0, page: 1, pageSize: 20 });
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailSearch, setDetailSearch] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => setMounted(true), []);

  const fetchReport = async (range = dateRange, branchId = selectedBranchId) => {
    if (!range?.[0] || !range?.[1]) {
      toast.current?.show({ severity: "warn", summary: "Tarih Seçimi", detail: "Başlangıç ve bitiş tarihini seçmelisiniz." });
      return;
    }

    const requestId = ++requestSequence.current;
    setLoading(true);
    try {
      const response = await ReportService.getFinancialOverviewReport(
        toLocalYmd(range[0]),
        toLocalYmd(range[1]),
        branchId
      );
      if (requestId === requestSequence.current) {
        setReport(getPayload(response));
      }
    } catch (error) {
      if (requestId === requestSequence.current) {
        setReport(null);
        toast.current?.show({
          severity: "error",
          summary: "Rapor Alınamadı",
          detail: error?.response?.data?.message || error?.message || "Mali durum raporu oluşturulamadı.",
          life: 5000,
        });
      }
    } finally {
      if (requestId === requestSequence.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchReport(createPresetRange("month"), 0);
    // İlk yüklemede varsayılan dönem bir kez sorgulanır.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPreset = (preset) => {
    const range = createPresetRange(preset);
    setSelectedPreset(preset);
    setDateRange(range);
    fetchReport(range, selectedBranchId);
  };

  const loadDetail = async (type, page = 1, search = "") => {
    setDetailLoading(true);
    try {
      const response = await ReportService.getFinancialOverviewDetails({
        type,
        startDate: toLocalYmd(dateRange[0]),
        endDate: toLocalYmd(dateRange[1]),
        branchId: selectedBranchId,
        search,
        page,
        pageSize: 20,
      });
      setDetailData(getPayload(response));
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Detaylar Alınamadı",
        detail: error?.response?.data?.message || error?.message || "İşlem detayları yüklenemedi.",
        life: 4000,
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const openDetail = (type) => {
    setDetailType(type);
    setDetailSearch("");
    setDetailData({ title: "İşlem Detayları", items: [], totalCount: 0, page: 1, pageSize: 20 });
    setDetailVisible(true);
    loadDetail(type, 1, "");
  };

  const changeStartDate = (startDate) => {
    setSelectedPreset("");
    setDateRange((currentRange) => {
      const currentEndDate = currentRange?.[1] || null;
      const endDate = startDate && currentEndDate && currentEndDate < startDate
        ? startDate
        : currentEndDate;
      return [startDate, endDate];
    });
  };

  const changeEndDate = (endDate) => {
    setSelectedPreset("");
    setDateRange((currentRange) => [currentRange?.[0] || null, endDate]);
  };

  const customerRows = useMemo(() => {
    const rows = report?.customerReceivables || [];
    const search = customerSearch.trim().toLocaleLowerCase("tr-TR");
    return search ? rows.filter((row) => `${row.name} ${row.phone || ""}`.toLocaleLowerCase("tr-TR").includes(search)) : rows;
  }, [report, customerSearch]);

  const supplierRows = useMemo(() => {
    const rows = report?.supplierPayables || [];
    const search = supplierSearch.trim().toLocaleLowerCase("tr-TR");
    return search ? rows.filter((row) => `${row.name} ${row.phone || ""}`.toLocaleLowerCase("tr-TR").includes(search)) : rows;
  }, [report, supplierSearch]);

  const trendData = useMemo(() => ({
    labels: (report?.trend || []).map((point) => {
      const date = new Date(point.periodStart);
      return Number.isNaN(date.getTime()) ? point.label : date.toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "2-digit" });
    }),
    datasets: [
      {
        label: "Satış",
        data: (report?.trend || []).map((point) => point.sales || 0),
        borderColor: "#10b981",
        backgroundColor: "rgba(16, 185, 129, 0.12)",
        pointBackgroundColor: "#10b981",
        tension: 0.35,
        fill: true,
      },
      {
        label: "Tahsilat",
        data: (report?.trend || []).map((point) => point.collections || 0),
        borderColor: "#6366f1",
        backgroundColor: "rgba(99, 102, 241, 0.08)",
        pointBackgroundColor: "#6366f1",
        tension: 0.35,
        fill: false,
      },
      {
        label: "Gider",
        data: (report?.trend || []).map((point) => point.expenses || 0),
        borderColor: "#f43f5e",
        backgroundColor: "rgba(244, 63, 94, 0.08)",
        pointBackgroundColor: "#f43f5e",
        tension: 0.35,
        fill: false,
      },
    ],
  }), [report]);

  const trendOptions = {
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        position: "top",
        align: "end",
        labels: { usePointStyle: true, boxWidth: 7, font: { size: 10, weight: "bold" } },
      },
      tooltip: {
        backgroundColor: "#0f172a",
        padding: 12,
        callbacks: { label: (context) => `${context.dataset.label}: ${formatMoney(context.raw)}` },
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 10 } },
      y: {
        beginAtZero: true,
        grid: { color: "rgba(148, 163, 184, 0.14)" },
        ticks: { callback: (value) => `${numberFormatter.format(value / 1000)} B` },
      },
    },
  };

  const exportCsv = () => {
    if (!report) return;
    const period = report.periodSummary || {};
    const position = report.position || {};
    const rows = [
      ["Mali Durum Özeti", report.branchName, `${formatDate(report.startDate)} - ${formatDate(report.endDate)}`],
      [],
      ["Dönem Özeti", "Tutar (TL)"],
      ["Toplam Satış", period.totalSales || 0],
      ["Perakende Satış", period.totalRetailSales || 0],
      ["Perakende İşlem Adedi", period.retailSaleCount || 0],
      ["Toplam Tahsilat", period.totalCollections || 0],
      ["Toplam Alış", period.totalPurchases || 0],
      ["Tedarikçi Ödemeleri", period.totalSupplierPayments || 0],
      ["Toplam Gider", period.totalExpenses || 0],
      ["Net Nakit Akışı", period.netCashFlow || 0],
      [],
      ["Dönem Sonu Pozisyonu", "Tutar (TL)"],
      ["Kasa / Banka", position.liquidAssets || 0],
      ["Müşteri Alacakları", position.customerReceivables || 0],
      ["Tedarikçi Borçları", position.supplierPayables || 0],
      ["Kredi Kartı Borçları", position.creditCardLiabilities || 0],
      ["Net Mali Pozisyon", position.netFinancialPosition || 0],
      [],
      ["Müşteri Alacakları"],
      ["Müşteri", "Toplam Alacak", "Gecikmiş", "En Yakın Vade", "Son Tahsilat", "Açık Kalem"],
      ...(report.customerReceivables || []).map((row) => [row.name, row.totalBalance, row.overdueAmount, formatDate(row.nextDueDate), formatDate(row.lastPaymentDate), row.openItemCount]),
      [],
      ["Tedarikçi Borçları"],
      ["Tedarikçi", "Toplam Borç", "Gecikmiş", "En Yakın Vade", "Son Ödeme", "Açık Kalem"],
      ...(report.supplierPayables || []).map((row) => [row.name, row.totalBalance, row.overdueAmount, formatDate(row.nextDueDate), formatDate(row.lastPaymentDate), row.openItemCount]),
    ];

    const content = `\uFEFF${rows.map((row) => row.map(escapeCsv).join(";")).join("\r\n")}`;
    const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `mali-durum-ozeti-${toLocalYmd(dateRange[0])}-${toLocalYmd(dateRange[1])}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  const period = report?.periodSummary || {};
  const previousPeriod = report?.previousPeriodSummary || {};
  const position = report?.position || {};
  const branchOptions = [
    { label: "Tüm Şubeler", value: 0 },
    ...(report?.branches || []).map((branch) => ({ label: branch.name, value: branch.id })),
  ];
  const retailSalesShare = Number(period.totalSales) > 0
    ? (Number(period.totalRetailSales || 0) / Number(period.totalSales)) * 100
    : 0;
  const customerSales = Math.max(0, Number(period.totalSales || 0) - Number(period.totalRetailSales || 0));
  const customerSalesShare = Math.max(0, 100 - retailSalesShare);
  const reportStartDate = report?.startDate ? new Date(report.startDate) : null;
  const reportEndDate = report?.endDate ? new Date(report.endDate) : null;
  const reportDayCount = reportStartDate && reportEndDate &&
    !Number.isNaN(reportStartDate.getTime()) && !Number.isNaN(reportEndDate.getTime())
    ? Math.max(1, Math.round((reportEndDate - reportStartDate) / 86400000) + 1)
    : 1;
  const averageDailySales = Number(period.totalSales || 0) / reportDayCount;
  const collectionRate = Number(period.totalSales) > 0
    ? (Number(period.totalCollections || 0) / Number(period.totalSales)) * 100
    : 0;
  const expenseRate = Number(period.totalSales) > 0
    ? (Number(period.totalExpenses || 0) / Number(period.totalSales)) * 100
    : 0;
  const peakSalesPoint = (report?.trend || []).reduce(
    (peak, point) => Number(point.sales || 0) > Number(peak?.sales || 0) ? point : peak,
    null
  );
  const peakPeriodLabel = (() => {
    if (!peakSalesPoint || Number(peakSalesPoint.sales || 0) <= 0) return "Satış yok";
    const date = new Date(peakSalesPoint.periodStart);
    if (Number.isNaN(date.getTime())) return peakSalesPoint.label || "-";
    if (reportDayCount <= 45) return date.toLocaleDateString("tr-TR", { day: "2-digit", month: "short" });
    if (reportDayCount <= 180) return `${date.toLocaleDateString("tr-TR", { day: "2-digit", month: "short" })} haftası`;
    return date.toLocaleDateString("tr-TR", { month: "long", year: "numeric" });
  })();
  const collectionComment = Number(period.totalSales || 0) <= 0
    ? "Seçilen dönemde satış olmadığı için tahsilat performansı karşılaştırılamıyor."
    : collectionRate >= 100
      ? `Dönem tahsilatı satışların %${collectionRate.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}'ine ulaştı; geçmiş alacak tahsilatları bu oranı yükseltmiş olabilir.`
      : collectionRate >= 70
        ? `Dönem tahsilatı, satışların %${collectionRate.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}'i seviyesinde; nakit dönüşümü güçlü görünüyor.`
        : collectionRate >= 40
          ? `Dönem tahsilatı, satışların %${collectionRate.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}'i seviyesinde; açık alacakların takibi önem taşıyor.`
          : `Dönem tahsilatı satışlara göre düşük (%${collectionRate.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}); vadesi yaklaşan alacakları kontrol etmek faydalı olabilir.`;
  const overdueReceivableRate = Number(position.customerReceivables) > 0
    ? (Number(report?.receivableAging?.overdue || 0) / Number(position.customerReceivables)) * 100
    : 0;
  const shortTermPayables = Number(report?.payableAging?.overdue || 0) +
    Number(report?.payableAging?.dueToday || 0) +
    Number(report?.payableAging?.nextSevenDays || 0);
  const paymentPressureRate = Number(position.liquidAssets) > 0
    ? (shortTermPayables / Number(position.liquidAssets)) * 100
    : shortTermPayables > 0 ? 999 : 0;
  const healthItems = [
    Number(period.totalSales || 0) <= 0
      ? { label: "Tahsilat Performansı", value: "Veri Yok", detail: "Dönem satışı bulunmuyor", tone: "slate", icon: "pi pi-minus" }
      : collectionRate >= 70
        ? { label: "Tahsilat Performansı", value: "İyi", detail: `%${collectionRate.toLocaleString("tr-TR", { maximumFractionDigits: 1 })} tahsilat/satış`, tone: "green", icon: "pi pi-check-circle" }
        : collectionRate >= 40
          ? { label: "Tahsilat Performansı", value: "Dikkat", detail: `%${collectionRate.toLocaleString("tr-TR", { maximumFractionDigits: 1 })} tahsilat/satış`, tone: "amber", icon: "pi pi-exclamation-circle" }
          : { label: "Tahsilat Performansı", value: "Riskli", detail: `%${collectionRate.toLocaleString("tr-TR", { maximumFractionDigits: 1 })} tahsilat/satış`, tone: "rose", icon: "pi pi-times-circle" },
    overdueReceivableRate <= 10
      ? { label: "Gecikmiş Alacak", value: "Kontrollü", detail: `%${overdueReceivableRate.toLocaleString("tr-TR", { maximumFractionDigits: 1 })} gecikmiş`, tone: "green", icon: "pi pi-check-circle" }
      : overdueReceivableRate <= 25
        ? { label: "Gecikmiş Alacak", value: "Dikkat", detail: `%${overdueReceivableRate.toLocaleString("tr-TR", { maximumFractionDigits: 1 })} gecikmiş`, tone: "amber", icon: "pi pi-exclamation-circle" }
        : { label: "Gecikmiş Alacak", value: "Riskli", detail: `%${overdueReceivableRate.toLocaleString("tr-TR", { maximumFractionDigits: 1 })} gecikmiş`, tone: "rose", icon: "pi pi-times-circle" },
    paymentPressureRate <= 50
      ? { label: "7 Günlük Ödeme Baskısı", value: "Rahat", detail: formatMoney(shortTermPayables), tone: "green", icon: "pi pi-shield" }
      : paymentPressureRate <= 100
        ? { label: "7 Günlük Ödeme Baskısı", value: "Dikkat", detail: formatMoney(shortTermPayables), tone: "amber", icon: "pi pi-exclamation-triangle" }
        : { label: "7 Günlük Ödeme Baskısı", value: "Yüksek", detail: formatMoney(shortTermPayables), tone: "rose", icon: "pi pi-exclamation-triangle" },
  ];
  const healthToneClasses = {
    green: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-300",
    amber: "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-300",
    rose: "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300",
    slate: "border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300",
  };

  const periodMetrics = [
    { label: "Toplam Satış", value: period.totalSales, previousValue: previousPeriod.totalSales, description: "Seçilen dönemin satış cirosu", icon: "pi pi-shopping-cart", tone: tones.emerald, detailType: "sales" },
    {
      label: "Perakende Satış",
      value: period.totalRetailSales,
      previousValue: previousPeriod.totalRetailSales,
      description: `${numberFormatter.format(period.retailSaleCount || 0)} işlem · Toplam satışın %${retailSalesShare.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}'i`,
      icon: "pi pi-shopping-bag",
      tone: tones.cyan,
      detailType: "retail_sales",
    },
    { label: "Toplam Tahsilat", value: period.totalCollections, previousValue: previousPeriod.totalCollections, description: "Müşterilerden alınan ödemeler", icon: "pi pi-arrow-down-left", tone: tones.violet, detailType: "collections" },
    { label: "Toplam Alış", value: period.totalPurchases, previousValue: previousPeriod.totalPurchases, description: "Tedarikçilerden yapılan alışlar", icon: "pi pi-download", tone: tones.blue, detailType: "purchases" },
    { label: "Tedarikçi Ödemeleri", value: period.totalSupplierPayments, previousValue: previousPeriod.totalSupplierPayments, description: "Tedarikçilere yapılan ödemeler", icon: "pi pi-arrow-up-right", tone: tones.amber, detailType: "supplier_payments" },
    { label: "Toplam Gider", value: period.totalExpenses, previousValue: previousPeriod.totalExpenses, description: "Dönem içinde kaydedilen giderler", icon: "pi pi-wallet", tone: tones.rose, detailType: "expenses" },
    { label: "Net Nakit Akışı", value: period.netCashFlow, previousValue: previousPeriod.netCashFlow, description: "Finans hesaplarındaki net hareket", icon: "pi pi-chart-line", tone: Number(period.netCashFlow) >= 0 ? tones.emerald : tones.rose, emphasis: true, detailType: "cash_flow" },
  ];

  const positionMetrics = [
    { label: "Kasa / Banka", value: position.liquidAssets, description: "Bitiş tarihindeki likit varlık", icon: "pi pi-building-columns", tone: tones.blue },
    { label: "Müşteri Alacakları", value: position.customerReceivables, description: "Tahsil edilmemiş açık alacak", icon: "pi pi-users", tone: tones.emerald },
    { label: "Tedarikçi Borçları", value: position.supplierPayables, description: "Ödenmemiş açık borç", icon: "pi pi-truck", tone: tones.amber },
    { label: "Kredi Kartı Borçları", value: position.creditCardLiabilities, description: "Şirket kartlarının borç bakiyesi", icon: "pi pi-credit-card", tone: tones.rose },
    { label: "Net Mali Pozisyon", value: position.netFinancialPosition, description: "Likit varlık + alacak − yükümlülük", icon: "pi pi-compass", tone: Number(position.netFinancialPosition) >= 0 ? tones.violet : tones.rose, emphasis: true },
  ];

  const downloadPdf = async () => {
    if (!report || pdfLoading) return;
    setPdfLoading(true);
    const element = document.createElement("div");
    const generatedText = report.generatedAt
      ? new Date(report.generatedAt).toLocaleString("tr-TR")
      : new Date().toLocaleString("tr-TR");
    const metricRows = periodMetrics.map((metric) => `
      <tr>
        <td>${escapeHtml(metric.label)}</td>
        <td style="text-align:right;font-weight:800">${escapeHtml(formatMoney(metric.value))}</td>
        <td style="text-align:right;color:#475569">${escapeHtml(buildComparison(metric.value, metric.previousValue).label)}</td>
      </tr>`).join("");
    const positionRows = positionMetrics.map((metric) => `
      <tr><td>${escapeHtml(metric.label)}</td><td style="text-align:right;font-weight:800">${escapeHtml(formatMoney(metric.value))}</td></tr>`).join("");
    const customerRowsPdf = (report.customerReceivables || []).slice(0, 10).map((row) => `
      <tr><td>${escapeHtml(row.name)}</td><td style="text-align:right">${escapeHtml(formatMoney(row.totalBalance))}</td><td style="text-align:right">${escapeHtml(formatMoney(row.overdueAmount))}</td></tr>`).join("");
    const supplierRowsPdf = (report.supplierPayables || []).slice(0, 10).map((row) => `
      <tr><td>${escapeHtml(row.name)}</td><td style="text-align:right">${escapeHtml(formatMoney(row.totalBalance))}</td><td style="text-align:right">${escapeHtml(formatMoney(row.overdueAmount))}</td></tr>`).join("");
    const healthRows = healthItems.map((item) => `
      <div style="flex:1;border:1px solid #e2e8f0;border-radius:10px;padding:10px">
        <div style="font-size:8px;color:#64748b;text-transform:uppercase;font-weight:800">${escapeHtml(item.label)}</div>
        <div style="margin-top:4px;font-size:14px;font-weight:900;color:#0f172a">${escapeHtml(item.value)}</div>
        <div style="margin-top:2px;font-size:9px;color:#64748b">${escapeHtml(item.detail)}</div>
      </div>`).join("");

    element.innerHTML = `
      <style>
        .financial-pdf-table td { padding: 7px; border-bottom: 1px solid #e2e8f0; }
        .financial-pdf-compact td { padding: 5px 6px; border-bottom: 1px solid #e2e8f0; }
      </style>
      <div style="width:760px;padding:28px;font-family:Arial,sans-serif;color:#0f172a;background:#fff">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #2563eb;padding-bottom:16px">
          <div><div style="font-size:24px;font-weight:900">Mali Durum Özeti</div><div style="font-size:11px;color:#64748b;margin-top:5px">${escapeHtml(report.branchName)} · ${escapeHtml(formatDate(report.startDate))} – ${escapeHtml(formatDate(report.endDate))}</div></div>
          <div style="font-size:10px;color:#64748b;text-align:right">TL bazlı rapor<br/>Oluşturulma: ${escapeHtml(generatedText)}</div>
        </div>
        <h2 style="font-size:14px;margin:20px 0 8px">Dönem Performansı</h2>
        <table class="financial-pdf-table" style="width:100%;border-collapse:collapse;font-size:10px"><thead><tr style="background:#eff6ff"><th style="text-align:left;padding:7px">Gösterge</th><th style="text-align:right;padding:7px">Tutar</th><th style="text-align:right;padding:7px">Önceki eş dönem</th></tr></thead><tbody>${metricRows}</tbody></table>
        <h2 style="font-size:14px;margin:20px 0 8px">Finansal Sağlık</h2><div style="display:flex;gap:8px">${healthRows}</div>
        <h2 style="font-size:14px;margin:20px 0 8px">${escapeHtml(formatDate(report.endDate))} İtibarıyla Mali Pozisyon</h2>
        <table class="financial-pdf-table" style="width:100%;border-collapse:collapse;font-size:10px"><tbody>${positionRows}</tbody></table>
        <div style="display:flex;gap:16px;margin-top:20px">
          <div style="width:50%"><h2 style="font-size:13px;margin:0 0 8px">En Yüksek 10 Müşteri Alacağı</h2><table class="financial-pdf-compact" style="width:100%;border-collapse:collapse;font-size:9px"><thead><tr style="background:#ecfdf5"><th style="text-align:left;padding:6px">Müşteri</th><th style="text-align:right">Toplam</th><th style="text-align:right">Gecikmiş</th></tr></thead><tbody>${customerRowsPdf || '<tr><td colspan="3" style="padding:8px">Kayıt yok</td></tr>'}</tbody></table></div>
          <div style="width:50%"><h2 style="font-size:13px;margin:0 0 8px">En Yüksek 10 Tedarikçi Borcu</h2><table class="financial-pdf-compact" style="width:100%;border-collapse:collapse;font-size:9px"><thead><tr style="background:#fffbeb"><th style="text-align:left;padding:6px">Tedarikçi</th><th style="text-align:right">Toplam</th><th style="text-align:right">Gecikmiş</th></tr></thead><tbody>${supplierRowsPdf || '<tr><td colspan="3" style="padding:8px">Kayıt yok</td></tr>'}</tbody></table></div>
        </div>
        <div style="margin-top:22px;padding-top:10px;border-top:1px solid #e2e8f0;font-size:8px;color:#64748b">Net mali pozisyon, muhasebesel net kâr veya şirket değeri değildir. Önceki dönem, seçilen dönemle aynı gün sayısına sahiptir.</div>
      </div>`;
    document.body.appendChild(element);
    try {
      await html2pdf().set({
        margin: 6,
        filename: `mali-durum-ozeti-${toLocalYmd(dateRange[0])}-${toLocalYmd(dateRange[1])}.pdf`,
        image: { type: "jpeg", quality: 0.96 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      }).from(element).save();
    } catch (error) {
      toast.current?.show({ severity: "error", summary: "PDF Oluşturulamadı", detail: error?.message || "PDF raporu hazırlanamadı.", life: 4000 });
    } finally {
      element.remove();
      setPdfLoading(false);
    }
  };

  const partyNameTemplate = (type) => (row) => (
    <button
      type="button"
      onClick={() => navigate(type === "customer" ? `/customers/detail/${row.id}` : `/suppliers/detail/${row.id}`)}
      className="cursor-pointer appearance-none border-0 bg-transparent p-0 text-left [font-family:inherit] focus-visible:outline-none focus-visible:underline"
    >
      <strong className="block text-xs text-blue-700 hover:underline dark:text-blue-300">{row.name}</strong>
      {row.phone && <span className="mt-0.5 block text-[10px] text-slate-500 dark:text-slate-400">{row.phone}</span>}
    </button>
  );

  const tableHeader = (type) => {
    const isCustomer = type === "customer";
    const search = isCustomer ? customerSearch : supplierSearch;
    const setSearch = isCustomer ? setCustomerSearch : setSupplierSearch;
    const count = isCustomer ? customerRows.length : supplierRows.length;
    return (
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="m-0 text-sm font-black text-slate-900 dark:text-white">{isCustomer ? "Kimden Alacağım Var?" : "Kime Borcum Var?"}</h3>
          <p className="mb-0 mt-1 text-[10px] font-semibold text-slate-500 dark:text-slate-400">{numberFormatter.format(count)} cari hesap listeleniyor</p>
        </div>
        <span className="p-input-icon-left w-full sm:w-56">
          <i className="pi pi-search" />
          <InputText value={search} onChange={(event) => setSearch(event.target.value)} placeholder={isCustomer ? "Müşteri ara..." : "Tedarikçi ara..."} className="w-full !rounded-xl !text-xs" />
        </span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] p-2 dark:bg-slate-950 md:p-4 print:bg-white print:p-0">
      <Toast ref={toast} appendTo={mounted ? document.body : "self"} />

      <header className="mb-5 flex flex-col gap-4 rounded-[2rem] border border-slate-200/80 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 md:flex-row md:items-center md:justify-between print:border-0 print:shadow-none">
        <div className="flex items-center gap-4">
          <span className="flex !h-14 !w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-600 to-blue-600 text-white shadow-lg shadow-indigo-500/20">
            <i className="pi pi-chart-pie text-xl" />
          </span>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="m-0 text-2xl font-black tracking-tight text-slate-900 dark:text-white">Mali Durum Özeti</h1>
              <span className="rounded-full bg-blue-50 px-2.5 py-1 text-[9px] font-black uppercase tracking-wider text-blue-700 dark:bg-blue-950/60 dark:text-blue-300">TL</span>
            </div>
            <p className="mb-0 mt-1 text-xs font-semibold text-slate-500 dark:text-slate-400">Dönem performansınızı ve güncel mali pozisyonunuzu tek ekranda görün.</p>
            {report?.generatedAt && (
              <p className="mb-0 mt-1.5 flex items-center gap-1.5 text-[9px] font-bold text-slate-400 dark:text-slate-500">
                <i className="pi pi-clock text-[8px]" aria-hidden="true" />
                Veriler {new Date(report.generatedAt).toLocaleString("tr-TR")} itibarıyla günceldir
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 print:hidden">
          <Button type="button" label="CSV İndir" icon="pi pi-file-excel" outlined severity="success" onClick={exportCsv} disabled={!report || loading} className="p-button-sm !rounded-xl !font-black" />
          <Button type="button" label="PDF İndir" icon="pi pi-file-pdf" outlined severity="danger" onClick={downloadPdf} loading={pdfLoading} disabled={!report || loading} className="p-button-sm !rounded-xl !font-black" />
          <Button type="button" label="Yazdır" icon="pi pi-print" outlined onClick={() => window.print()} disabled={!report || loading} className="p-button-sm !rounded-xl !font-black" />
        </div>
      </header>

      <section className="mb-5 rounded-[1.75rem] border border-slate-200/80 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 print:hidden">
        <div className="mb-4 flex flex-wrap gap-2">
          {periodPresets.map((preset) => (
            <button
              key={preset.value}
              type="button"
              onClick={() => applyPreset(preset.value)}
              aria-pressed={selectedPreset === preset.value}
              className={`inline-flex cursor-pointer appearance-none items-center gap-1.5 rounded-xl border border-solid px-3 py-2 text-[10px] font-black [font-family:inherit] transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 dark:focus-visible:ring-white dark:focus-visible:ring-offset-slate-900 ${selectedPreset === preset.value ? preset.activeClass : preset.idleClass}`}
            >
              <i className={`${preset.icon} text-[10px]`} aria-hidden="true" />
              {preset.label}
            </button>
          ))}
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(170px,1fr)_minmax(170px,1fr)_minmax(180px,0.85fr)_auto] lg:items-end">
          <div>
            <label htmlFor="financial-overview-start-date" className="mb-1.5 block text-[9px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400">Başlangıç Tarihi</label>
            <Calendar
              inputId="financial-overview-start-date"
              value={dateRange?.[0] || null}
              onChange={(event) => changeStartDate(event.value)}
              readOnlyInput
              showIcon
              dateFormat="dd.mm.yy"
              maxDate={new Date()}
              className="w-full"
              inputClassName="!rounded-l-xl !text-xs !font-bold"
            />
          </div>
          <div>
            <label htmlFor="financial-overview-end-date" className="mb-1.5 block text-[9px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400">Bitiş Tarihi</label>
            <Calendar
              inputId="financial-overview-end-date"
              value={dateRange?.[1] || null}
              onChange={(event) => changeEndDate(event.value)}
              readOnlyInput
              showIcon
              dateFormat="dd.mm.yy"
              minDate={dateRange?.[0] || undefined}
              maxDate={new Date()}
              className="w-full"
              inputClassName="!rounded-l-xl !text-xs !font-bold"
            />
          </div>
          <div>
            <label htmlFor="financial-overview-branch" className="mb-1.5 block text-[9px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400">Şube</label>
            <Dropdown
              inputId="financial-overview-branch"
              value={selectedBranchId}
              options={branchOptions}
              onChange={(event) => setSelectedBranchId(event.value)}
              className="w-full !rounded-xl"
            />
          </div>
          <Button type="button" label="Raporu Güncelle" icon="pi pi-refresh" onClick={() => fetchReport()} loading={loading} className="!h-[42px] !rounded-xl !border-0 !bg-blue-600 !px-5 !text-xs !font-black" />
        </div>
      </section>

      {loading && !report ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {[0, 1, 2, 3, 4, 5, 6].map((key) => <Skeleton key={key} height="7rem" borderRadius="18px" />)}
          </div>
          <Skeleton height="22rem" borderRadius="24px" />
        </div>
      ) : report ? (
        <div className={loading ? "pointer-events-none opacity-60" : ""}>
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <h2 className="m-0 text-base font-black text-slate-900 dark:text-white">Dönem Performansı</h2>
              <p className="mb-0 mt-1 text-[10px] font-semibold text-slate-500 dark:text-slate-400">{formatDate(report.startDate)} – {formatDate(report.endDate)} · {report.branchName}</p>
            </div>
            <span className="hidden rounded-full bg-slate-100 px-2.5 py-1 text-[8px] font-bold text-slate-500 dark:bg-slate-800 dark:text-slate-400 sm:inline-flex">
              Karşılaştırma: {formatDate(report.previousPeriodStartDate)} – {formatDate(report.previousPeriodEndDate)}
            </span>
          </div>
          <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
            {periodMetrics.map((metric) => (
              <MetricCard
                key={metric.label}
                {...metric}
                comparison={buildComparison(metric.value, metric.previousValue)}
                onClick={() => openDetail(metric.detailType)}
              />
            ))}
          </div>

          <section className="mb-6 rounded-[1.5rem] border border-slate-200/80 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <h2 className="m-0 text-sm font-black text-slate-900 dark:text-white">Finansal Sağlık</h2>
                <p className="mb-0 mt-1 text-[9px] font-semibold text-slate-500 dark:text-slate-400">Tahsilat, gecikmiş alacak ve kısa vadeli ödeme yükünün hızlı değerlendirmesi</p>
              </div>
              <i className="pi pi-heart-fill text-sm text-rose-500" aria-hidden="true" />
            </div>
            <div className="grid gap-2.5 sm:grid-cols-3">
              {healthItems.map((item) => (
                <div key={item.label} className={`flex items-center gap-3 rounded-2xl border border-solid px-3.5 py-3 ${healthToneClasses[item.tone]}`}>
                  <span className="flex !h-8 !w-8 shrink-0 items-center justify-center rounded-xl bg-white/70 dark:bg-slate-900/40" aria-hidden="true">
                    <i className={`${item.icon} text-xs`} />
                  </span>
                  <div className="min-w-0">
                    <span className="block truncate text-[8px] font-black uppercase tracking-wider opacity-75">{item.label}</span>
                    <strong className="mt-0.5 block text-xs font-black">{item.value}</strong>
                    <span className="mt-0.5 block truncate text-[8px] font-bold opacity-80">{item.detail}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <div className="mb-3">
            <h2 className="m-0 text-base font-black text-slate-900 dark:text-white">{formatDate(report.endDate)} İtibarıyla Mali Pozisyon</h2>
            <p className="mb-0 mt-1 text-[10px] font-semibold text-slate-500 dark:text-slate-400">Dönem sonundaki likit varlık, alacak ve yükümlülüklerin net görünümü</p>
          </div>
          <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
            {positionMetrics.map((metric) => <MetricCard key={metric.label} {...metric} />)}
          </div>

          <div className="mb-6 grid gap-5 xl:grid-cols-[1.25fr_0.75fr]">
            <section className="rounded-[1.75rem] border border-slate-200/80 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="mb-4">
                <h3 className="m-0 text-base font-black text-slate-900 dark:text-white">Satış, Tahsilat ve Gider Eğilimi</h3>
                <p className="mb-0 mt-1 text-[10px] font-semibold text-slate-500 dark:text-slate-400">Seçilen dönemin zaman içindeki finansal hareketi</p>
              </div>
              <div className="h-[330px]">
                <Chart type="line" data={trendData} options={trendOptions} className="h-full w-full" />
              </div>

              <div className="mt-5 border-t border-slate-200/80 pt-5 dark:border-slate-800">
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div>
                    <h4 className="m-0 text-xs font-black text-slate-900 dark:text-white">Dönem İçgörüleri</h4>
                    <p className="mb-0 mt-1 text-[9px] font-semibold text-slate-500 dark:text-slate-400">Grafikteki hareketlerin kısa performans özeti</p>
                  </div>
                  <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-[8px] font-black uppercase tracking-wider text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300">
                    {numberFormatter.format(reportDayCount)} gün
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2.5 2xl:grid-cols-4">
                  <InsightCard
                    label="Günlük Ortalama Satış"
                    value={formatMoney(averageDailySales)}
                    detail={`${numberFormatter.format(reportDayCount)} günlük ortalama`}
                    icon="pi pi-chart-bar"
                    color="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-300"
                  />
                  <InsightCard
                    label="Tahsilat / Satış"
                    value={`%${collectionRate.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}`}
                    detail={formatMoney(period.totalCollections)}
                    icon="pi pi-percentage"
                    color="bg-indigo-100 text-indigo-700 dark:bg-indigo-900/60 dark:text-indigo-300"
                  />
                  <InsightCard
                    label="Gider / Satış"
                    value={`%${expenseRate.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}`}
                    detail={formatMoney(period.totalExpenses)}
                    icon="pi pi-wallet"
                    color="bg-rose-100 text-rose-700 dark:bg-rose-900/60 dark:text-rose-300"
                  />
                  <InsightCard
                    label={reportDayCount <= 45 ? "En Güçlü Satış Günü" : reportDayCount <= 180 ? "En Güçlü Satış Haftası" : "En Güçlü Satış Ayı"}
                    value={peakPeriodLabel}
                    detail={formatMoney(peakSalesPoint?.sales || 0)}
                    icon="pi pi-bolt"
                    color="bg-amber-100 text-amber-700 dark:bg-amber-900/60 dark:text-amber-300"
                  />
                </div>

                <div className="mt-3 rounded-2xl border border-slate-200/80 bg-white p-3.5 dark:border-slate-700/80 dark:bg-slate-900/50">
                  <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <span className="block text-[9px] font-black uppercase tracking-wider text-slate-600 dark:text-slate-300">Satış Dağılımı</span>
                      <span className="mt-0.5 block text-[8px] font-semibold text-slate-500 dark:text-slate-400">Perakende ve cari müşteriye yapılan satışlar</span>
                    </div>
                    <div className="flex items-center gap-3 text-[9px] font-black">
                      <span className="text-cyan-700 dark:text-cyan-300">Perakende %{retailSalesShare.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}</span>
                      <span className="text-violet-700 dark:text-violet-300">Cari %{customerSalesShare.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}</span>
                    </div>
                  </div>
                  <div className="flex h-2.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800" aria-label={`Perakende satış oranı yüzde ${retailSalesShare.toFixed(1)}, cari müşteri satış oranı yüzde ${customerSalesShare.toFixed(1)}`}>
                    <span className="h-full bg-cyan-500 transition-[width]" style={{ width: `${Math.min(100, Math.max(0, retailSalesShare))}%` }} />
                    <span className="h-full bg-violet-500 transition-[width]" style={{ width: `${Math.min(100, Math.max(0, customerSalesShare))}%` }} />
                  </div>
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[9px] font-bold text-slate-500 dark:text-slate-400">
                    <span>Perakende: <strong className="text-slate-700 dark:text-slate-200">{formatMoney(period.totalRetailSales)}</strong></span>
                    <span>Cari müşteri: <strong className="text-slate-700 dark:text-slate-200">{formatMoney(customerSales)}</strong></span>
                  </div>
                </div>

                <div className="mt-3 flex items-start gap-2.5 rounded-2xl border border-blue-100 bg-blue-50/70 px-3.5 py-3 text-[9px] font-semibold leading-relaxed text-blue-800 dark:border-blue-900/60 dark:bg-blue-950/30 dark:text-blue-300">
                  <i className="pi pi-lightbulb mt-0.5 shrink-0" aria-hidden="true" />
                  <span>{collectionComment}</span>
                </div>
              </div>
            </section>

            <div className="grid gap-5">
              <AgingPanel title="Alacak Vade Dağılımı" subtitle="Müşterilerden beklenen tahsilatlar" data={report.receivableAging} color="bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300" />
              <AgingPanel title="Borç Vade Dağılımı" subtitle="Tedarikçilere yapılacak ödemeler" data={report.payableAging} color="bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300" />
            </div>
          </div>

          <div className="grid gap-5 2xl:grid-cols-2">
            <section className="overflow-hidden rounded-[1.75rem] border border-slate-200/80 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <DataTable value={customerRows} header={tableHeader("customer")} paginator rows={10} rowsPerPageOptions={[10, 25, 50]} emptyMessage="Açık müşteri alacağı bulunmuyor." className="p-datatable-sm" responsiveLayout="scroll" sortField="totalBalance" sortOrder={-1}>
                <Column header="Müşteri" body={partyNameTemplate("customer")} sortable field="name" />
                <Column field="totalBalance" header="Toplam Alacak" body={(row) => <strong className="text-emerald-700 dark:text-emerald-300">{formatMoney(row.totalBalance)}</strong>} sortable />
                <Column field="overdueAmount" header="Gecikmiş" body={(row) => <span className={Number(row.overdueAmount) > 0 ? "font-black text-rose-600" : "text-slate-400"}>{formatMoney(row.overdueAmount)}</span>} sortable />
                <Column field="nextDueDate" header="En Yakın Vade" body={(row) => formatDate(row.nextDueDate)} sortable />
                <Column field="lastPaymentDate" header="Son Tahsilat" body={(row) => formatDate(row.lastPaymentDate)} sortable />
                <Column field="openItemCount" header="Kalem" body={(row) => numberFormatter.format(row.openItemCount || 0)} sortable />
              </DataTable>
            </section>

            <section className="overflow-hidden rounded-[1.75rem] border border-slate-200/80 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <DataTable value={supplierRows} header={tableHeader("supplier")} paginator rows={10} rowsPerPageOptions={[10, 25, 50]} emptyMessage="Açık tedarikçi borcu bulunmuyor." className="p-datatable-sm" responsiveLayout="scroll" sortField="totalBalance" sortOrder={-1}>
                <Column header="Tedarikçi" body={partyNameTemplate("supplier")} sortable field="name" />
                <Column field="totalBalance" header="Toplam Borç" body={(row) => <strong className="text-amber-700 dark:text-amber-300">{formatMoney(row.totalBalance)}</strong>} sortable />
                <Column field="overdueAmount" header="Gecikmiş" body={(row) => <span className={Number(row.overdueAmount) > 0 ? "font-black text-rose-600" : "text-slate-400"}>{formatMoney(row.overdueAmount)}</span>} sortable />
                <Column field="nextDueDate" header="En Yakın Vade" body={(row) => formatDate(row.nextDueDate)} sortable />
                <Column field="lastPaymentDate" header="Son Ödeme" body={(row) => formatDate(row.lastPaymentDate)} sortable />
                <Column field="openItemCount" header="Kalem" body={(row) => numberFormatter.format(row.openItemCount || 0)} sortable />
              </DataTable>
            </section>
          </div>

          <div className="mt-5 rounded-2xl border border-blue-100 bg-blue-50/70 px-4 py-3 text-[10px] font-semibold leading-relaxed text-blue-800 dark:border-blue-900/60 dark:bg-blue-950/30 dark:text-blue-300">
            <i className="pi pi-info-circle mr-2" />
            Net mali pozisyon; kasa/banka bakiyeleri ile müşteri alacaklarının toplamından tedarikçi ve şirket kredi kartı borçlarının çıkarılmasıyla hesaplanır. Şirket değeri veya muhasebesel net kâr değildir.
          </div>
        </div>
      ) : (
        <div className="rounded-[1.75rem] border border-slate-200 bg-white p-10 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <i className="pi pi-chart-pie text-4xl text-slate-300" />
          <h2 className="mb-0 mt-4 text-base font-black text-slate-700 dark:text-slate-200">Rapor görüntülenemedi</h2>
          <p className="mb-4 mt-1 text-xs text-slate-500">Filtreleri kontrol edip yeniden deneyebilirsiniz.</p>
          <Button label="Tekrar Dene" icon="pi pi-refresh" onClick={() => fetchReport()} className="p-button-sm !rounded-xl" />
        </div>
      )}

      <Dialog
        visible={detailVisible}
        onHide={() => setDetailVisible(false)}
        header={detailData?.title || "İşlem Detayları"}
        modal
        dismissableMask
        className="w-[96vw] lg:w-[78vw] xl:w-[68vw]"
        contentClassName="!p-0"
      >
        <div className="border-b border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="m-0 text-[10px] font-bold text-slate-500 dark:text-slate-400">
                {formatDate(report?.startDate)} – {formatDate(report?.endDate)} · {report?.branchName}
              </p>
              <p className="mb-0 mt-1 text-[9px] font-semibold text-slate-400">{numberFormatter.format(detailData?.totalCount || 0)} işlem bulundu</p>
            </div>
            <div className="flex w-full gap-2 sm:w-80">
              <span className="p-input-icon-left min-w-0 flex-1">
                <i className="pi pi-search" />
                <InputText
                  value={detailSearch}
                  onChange={(event) => setDetailSearch(event.target.value)}
                  onKeyDown={(event) => { if (event.key === "Enter") loadDetail(detailType, 1, detailSearch); }}
                  placeholder="Belge, cari veya açıklama ara..."
                  maxLength={200}
                  className="w-full !rounded-xl !text-xs"
                />
              </span>
              <Button type="button" icon="pi pi-search" aria-label="Detaylarda ara" onClick={() => loadDetail(detailType, 1, detailSearch)} className="p-button-sm !rounded-xl" />
            </div>
          </div>
        </div>
        <DataTable
          value={detailData?.items || []}
          loading={detailLoading}
          lazy
          paginator
          first={((detailData?.page || 1) - 1) * (detailData?.pageSize || 20)}
          rows={detailData?.pageSize || 20}
          totalRecords={detailData?.totalCount || 0}
          onPage={(event) => loadDetail(detailType, event.page + 1, detailSearch)}
          emptyMessage="Bu filtrelerle eşleşen işlem bulunmuyor."
          className="p-datatable-sm"
          responsiveLayout="scroll"
        >
          <Column field="date" header="Tarih" body={(row) => formatDate(row.date)} style={{ minWidth: "7rem" }} />
          <Column field="documentNo" header="Belge No" body={(row) => row.documentNo || "-"} style={{ minWidth: "7rem" }} />
          <Column field="partyName" header="Cari / Hesap" style={{ minWidth: "11rem" }} />
          <Column field="branchName" header="Şube" style={{ minWidth: "8rem" }} />
          <Column field="description" header="Açıklama" body={(row) => row.description || "-"} style={{ minWidth: "14rem" }} />
          <Column
            field="amount"
            header="Tutar"
            body={(row) => <strong className={Number(row.amount) < 0 ? "text-rose-600" : "text-emerald-700 dark:text-emerald-300"}>{formatMoney(row.amount)}</strong>}
            style={{ minWidth: "9rem", textAlign: "right" }}
          />
        </DataTable>
      </Dialog>
    </div>
  );
}
